/* =============================================================================
 * Guardas de rota.
 *
 * Devolvem true (pode entrar) ou { redirecionar }. Sao proteccao de EXPERIENCIA:
 * evitam que o usuario caia numa tela que nao vai funcionar. A restricao de
 * verdade tem que estar no backend - ver a nota em capacidades.js.
 * ========================================================================== */

import { sessao } from '@shared/sessao/sessao.js';
import { capacidades } from '@shared/sessao/capacidades.js';
import { idCompeticaoAtual, selecionarCompeticao } from '@shared/sessao/competicao-atual.js';
import { inicioDoUsuario } from '@shared/sessao/inicio.js';
import { paraInteiro } from '@shared/lib/valores.js';

export function exigirLogin(contexto) {
    if (sessao.usuario) return true;
    //Preserva para onde a pessoa queria ir, para voltar depois do login.
    return { redirecionar: `/usuario/entrar?volta=${encodeURIComponent(contexto.url)}` };
}

/**
 * O contrario de `exigirLogin`: a tela so' faz sentido para quem AINDA nao
 * entrou.
 *
 * Sem isto, um link antigo de "Entrar" - o do cabecalho antes de a casca
 * repintar, o que alguem deixou salvo - abria um formulario de login para quem
 * ja' esta' conectado. Nao ha' resposta boa para essa tela: preenche-la de novo
 * nao muda nada, e ficar olhando para ela parece que a sessao caiu.
 *
 * Respeita o `?volta=` pelo mesmo motivo que a tela de login respeita: quem
 * chegou aqui por uma guarda de rota queria ir a OUTRO lugar, e ja' tem
 * permissao para ele.
 */
export function apenasVisitante(contexto) {
    if (!sessao.usuario) return true;
    const volta = contexto.query?.volta;
    //A MESMA REGRA DO POS-LOGIN, e nao um destino proprio: quem cai aqui ja'
    //autenticado esta' na situacao de quem acabou de entrar. Ver sessao/inicio.js.
    return { redirecionar: volta && volta.startsWith('/') ? volta : inicioDoUsuario() };
}

export function exigirCapacidade(teste) {
    return (contexto) => {
        if (!sessao.usuario) return exigirLogin(contexto);
        return teste(capacidades(), contexto.params) ? true : { redirecionar: '/' };
    };
}

/**
 * Exige uma competicao selecionada, e e' quem NORMALIZA a selecao: depois desta
 * guarda, a pagina so' precisa de `idCompeticaoAtual()`.
 *
 * A ordem de precedencia e' a mesma do filtro de categoria (ver
 * shared/ui/componentes/filtro-categoria.js): o que veio na URL ganha do que
 * esta' salvo. E' isso que mantem o link compartilhado abrindo a competicao
 * certa mesmo para quem tinha outra escolhida.
 *
 * @param {string|null} opcoes.deParam nome do parametro de rota que carrega o
 *   IdCompeticao. `/competicoes/partida/:id` NAO tem um: o `id` dali e' da
 *   partida, e ai' so' resta a selecao salva.
 * @param {(contexto) => string} opcoes.escolherEm para onde mandar escolher. A
 *   lista publica so' mostra competicoes liberadas para o site, e o clube pode
 *   estar numa que nao esta' - por isso a gestao manda escolher no painel do
 *   proprio clube.
 */
export function exigirCompeticao({ deParam = null, escolherEm = () => '/competicoes' } = {}) {
    return (contexto) => {
        const daUrl = deParam ? paraInteiro(contexto.params[deParam]) : 0;
        if (daUrl > 0) {
            selecionarCompeticao(daUrl);
            return true;
        }

        if (idCompeticaoAtual() > 0) return true;

        //Sem selecao nao da' para montar a tela. Manda escolher, guardando para
        //onde a pessoa queria ir - a tela de escolha devolve ela para ca'.
        const destino = escolherEm(contexto);
        const separador = destino.includes('?') ? '&' : '?';
        return { redirecionar: `${destino}${separador}volta=${encodeURIComponent(contexto.url)}` };
    };
}

/** Encadeia guardas: a primeira que barrar decide o redirecionamento. */
export function todas(...guardas) {
    return (contexto) => {
        for (const guarda of guardas) {
            const resultado = guarda(contexto);
            if (resultado !== true) return resultado;
        }
        return true;
    };
}
